import React, { useEffect, useMemo, useState } from 'react'

import { useUI } from 'Editor'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'

import { LayersIcon } from '@radix-ui/react-icons'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'

import Loader from '@/components/ui/Loader'
import { cn } from '@/lib/utils'
import MyDataTable from 'components/ui/my-data-table'
import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { DataTableRowActions, DataTableRowName } from './data-table-row-actions'
import DataTableToolbar from './data-table-toolbar'
import BindLayersDialog from './bind-layers-dialog'

export const columns = [
  {
    accessorKey: 'name',
    header: <div></div>,
    cell: ({ row }) => <DataTableRowName row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'mark',
    header: <div className="flex items-center">Color count</div>,
    cell: ({ row }) => {
      const toning = row.original
      return (
        <div className="flex items-center gap-2">{toning.colors?.length}</div>
      )
    },
  },
  {
    id: 'linkedLayerCount',
    header: <div className="flex items-center ">Linked layers</div>,
    cell: ({ row }) => {
      const toning = row.original
      return (
        <div
          className={cn(
            'flex items-center gap-2',
            toning.layers?.length <= 0 ? 'text-destructive' : '',
          )}
        >
          <LayersIcon className={cn('w-5 h-5')} />
          <span>{toning.layers?.length}</span>
          <BindLayersDialog toning={toning} />
        </div>
      )
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
]

const ToningsPalnel = () => {
  const { tid } = useParams()

  const {
    data: tonings,
    isLoading,
    isValidating,
  } = useSWR(['/tonings', { tid }])
  const { data: layers } = useSWR(['/layers', { tid }])
  const toningId = useUI((state) => state.toningId)
  const toggleToning = useUI((state) => state.toggleToning)

  const [rowSelection, setRowSelection] = useState(
    toningId ? { [toningId]: true } : {},
  )

  const data = useMemo(() => {
    return (
      tonings?.map((toning) => ({
        ...toning,
        layers: layers?.filter((layer) => layer.toning === toning.id),
      })) ?? []
    )
  }, [tonings, layers])

  // 检查是否有 toning 没有绑定 layer
  const hasUnboundTonings = useMemo(() => {
    return data.some((toning) => !toning.layers || toning.layers.length === 0)
  }, [data])

  useEffect(() => {
    if (toningId) {
      setRowSelection({ [toningId]: true })
    }
  }, [toningId])

  useEffect(() => {
    const firstKey = Object.keys(rowSelection)[0]
    if (firstKey) {
      toggleToning(firstKey)
    }
  }, [rowSelection, toggleToning])

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
    },
    getRowId: (row) => row.id,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-3">
      <div className="shrink-0">
        <DataTableToolbar table={table} tonings={tonings} />
      </div>
      {hasUnboundTonings && (
        <div className="flex shrink-0 items-center gap-2 rounded-lg bg-destructive px-4 py-3 text-destructive-foreground">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span className="font-bold">存在染色没有绑定图层， 请先绑定</span>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
        {(isLoading || isValidating) && (
          <div className="ml-2 mt-2">
            <Loader>Load tonings...</Loader>
          </div>
        )}
        <div className="h-full overflow-y-auto">
          <MyDataTable table={table} />
        </div>
      </div>
    </div>
  )
}

export default ToningsPalnel
